"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Clock,
  GripVertical,
  Info,
  LoaderCircle,
  Network,
  Play,
  Plus,
  RefreshCw,
  Sparkles,
  Star,
  Trash2,
  X
} from "lucide-react";

// Types mirror the DB-backed shape produced by lib/plotSeedData.ts.

export type SeedCandidate = {
  id: number;
  index: number;
  trope: string;
  iterationKey: string;
  preset: string;
  title: string;
  logline: string;
  plotText: string;
  cast: string;
  ko: { title: string; logline: string; plot: string } | null;
  starred: number;
  archived: boolean;
  steeringText: string;
  used: boolean;
  guideFiles: string[];
  generationCli: string;
  generationModel: string;
  generationEffort: string;
  generationUsage: {
    inputTokens: number;
    outputTokens: number;
    totalInputTokens: number;
    estimatedApiCostUsd: number;
  } | null;
};

export type OccupationItem = {
  id: number;
  name: string;
  gender: "female" | "male";
  hasContent: boolean;
  prefetching: boolean;
};

export type SeedDetail = {
  id: number;
  title: string;
  direction: string;
  plots: SeedCandidate[];
};

export type PlotSeedData = {
  occupations: OccupationItem[];
  selectedOccupation: string;
  selectedOccupationId: number | null;
  project: SeedDetail | null;
  expectedPlotPresets: string[];
  plotCli: "codex" | "claude";
  translationCli: "codex" | "claude";
};

const EXPLORE_SPLIT_STORAGE_KEY = "story37.plotSeed.exploreRatio";
const SHOW_ARCHIVED_STORAGE_KEY = "story37.plotSeed.showArchived";
const BATCH_INTERVAL_TIME_STORAGE_KEY = "story37.plotSeed.batchIntervalTime";
const PLOT_PRESETS = [
  "Quest",
  "Adventure",
  "Pursuit",
  "Rescue",
  "Escape",
  "Revenge",
  "The Riddle",
  "Rivalry",
  "Underdog",
  "Temptation",
  "Metamorphosis",
  "Transformation",
  "Maturation",
  "Love",
  "Forbidden Love",
  "Sacrifice",
  "Discovery",
  "Wretched Excess",
  "Ascension",
  "Descension"
];

type PlotMobileDetailHistoryState = {
  story37PlotMobileDetail?: boolean;
  story37PlotId?: number;
};

type SourceDoc = {
  title: string;
  subtitle?: string;
  markdown: string;
};

type SelectPlotOptions = {
  replaceMobileHistory?: boolean;
  resetMobileLanguage?: boolean;
};

function isPlotMobileDetailHistoryState(state: unknown): state is PlotMobileDetailHistoryState {
  return Boolean(state && typeof state === "object" && (state as PlotMobileDetailHistoryState).story37PlotMobileDetail);
}

// Draggable split whose ratio persists in localStorage (loaded on mount, saved on
// drag end). axis "x" = left/right for the plot candidate/detail split.
function useSplit(storageKey: string, defaultRatio: number, axis: "x" | "y") {
  const [ratio, setRatio] = useState(defaultRatio);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  useEffect(() => {
    try {
      const saved = Number(localStorage.getItem(storageKey));
      if (saved >= 0.15 && saved <= 0.85) {
        setRatio(saved);
      }
    } catch {
      // ignore storage failures
    }
  }, [storageKey]);
  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    dragging.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!dragging.current || !containerRef.current) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const next =
      axis === "x" ? (event.clientX - rect.left) / rect.width : (event.clientY - rect.top) / rect.height;
    setRatio(Math.min(0.85, Math.max(0.15, next)));
  }
  function onPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    dragging.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
      localStorage.setItem(storageKey, String(ratio));
    } catch {
      // ignore storage failures
    }
  }
  return { ratio, containerRef, handle: { onPointerDown, onPointerMove, onPointerUp } };
}

function formatTokenCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0";
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
  return String(Math.round(value));
}

function formatApiCost(usage: SeedCandidate["generationUsage"]): string {
  if (!usage) return "";
  const cost = usage.estimatedApiCostUsd;
  const costText = cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(3)}`;
  return `api est: ${costText} · in ${formatTokenCount(usage.totalInputTokens)} / out ${formatTokenCount(usage.outputTokens)}`;
}

function OutlinedDownArrowIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M6.6 2.5h2.8v5h3.2L8 13.2 3.4 7.5h3.2z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function displayIterationCode(value: string): string {
  const market = value.match(/^Market Trope 0?(\d+)$/);
  if (market) return `M${Number(market[1])}`;
  const story = value.match(/^Story Engine 0?(\d+)$/);
  if (story) return `S${Number(story[1])}`;
  return value;
}

function displayStoryEngineName(value: string): string {
  return value
    .replace(/^Market Trope 0?\d+[A-Z]?\s+-\s+/, "")
    .replace(/^Story Engine 0?\d+\s+-\s+/, "")
    .trim();
}

function displayPlotPattern(plot: SeedCandidate): string {
  const code = displayIterationCode(plot.iterationKey || "");
  const name = displayStoryEngineName(plot.preset || "");
  if (code && name && code !== name && displayIterationCode(name) !== code) {
    return `${code} · ${name}`;
  }
  return code || name || "—";
}

function plotSelectionKey(plot: SeedCandidate): string {
  return (plot.iterationKey || plot.preset || plot.trope || "").trim().toLowerCase();
}

function choosePlotForSelection(plots: SeedCandidate[], selectedId: number | null, selectedKey: string): SeedCandidate | null {
  if (plots.length === 0) {
    return null;
  }
  const exact = selectedId != null ? plots.find((plot) => plot.id === selectedId) : null;
  if (exact) {
    return exact;
  }
  const activePlots = plots.filter((plot) => !plot.archived);
  const key = selectedKey.trim().toLowerCase();
  if (key) {
    const activeMatch = activePlots.find((plot) => plotSelectionKey(plot) === key);
    if (activeMatch) {
      return activeMatch;
    }
  }
  return activePlots[0] ?? plots[0] ?? null;
}

type SplitControl = ReturnType<typeof useSplit>;
type PlotLanguageMode = "ko" | "en";

async function postAction(action: string, payload: Record<string, unknown>) {
  const response = await fetch("/api/plot-seed", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action, ...payload })
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || `plot-seed ${action} failed (${response.status})`);
  }
}

async function fetchSourceDoc(params: URLSearchParams): Promise<SourceDoc> {
  const response = await fetch(`/api/plot-seed/source-doc?${params.toString()}`, { cache: "no-store" });
  const body = (await response.json().catch(() => null)) as SourceDoc & { error?: string } | null;
  if (!response.ok || !body) {
    throw new Error(body?.error || "source document failed");
  }
  return body;
}

function parseBatchStartTime(value: string): { hour: number; minute: number } | null {
  const text = value.trim();
  const match = text.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;
  return { hour: Number(match[1]), minute: Number(match[2]) };
}

function normalizeBatchStartTime(value: string): string | null {
  const parsed = parseBatchStartTime(value);
  if (!parsed) return null;
  return `${String(parsed.hour).padStart(2, "0")}:${String(parsed.minute).padStart(2, "0")}`;
}

function formatLocalClock(value: Date): string {
  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
}

function formatBatchScheduledClock(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatLocalClock(date);
}

function formatBatchIntervalAnchorClock(anchor: string | null, minutes: number): string {
  if (!anchor) return "";
  const date = new Date(anchor);
  if (Number.isNaN(date.getTime())) return "";
  return formatLocalClock(new Date(date.getTime() + Math.max(1, minutes || 300) * 60 * 1000));
}

function defaultBatchIntervalTime(): string {
  const next = new Date(Date.now() + 5 * 60 * 60 * 1000);
  next.setSeconds(0, 0);
  return formatLocalClock(next);
}

function formatBatchCountdown(seconds: number | null): string {
  if (seconds == null) return "";
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours}h${rest ? ` ${rest}m` : ""}` : `${minutes}m`;
}

export function PlotSeedBrowser({ data }: { data: PlotSeedData }) {
  const router = useRouter();
  const { project } = data;
  const [selectedPlotId, setSelectedPlotId] = useState<number | null>(null);
  const [selectedPlotKey, setSelectedPlotKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatingAction, setGeneratingAction] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState("stopped");
  const [batchScheduledAt, setBatchScheduledAt] = useState<string | null>(null);
  const [batchSeconds, setBatchSeconds] = useState<number | null>(null);
  const [batchCli, setBatchCli] = useState("");
  const [batchIntervalEnabled, setBatchIntervalEnabled] = useState(false);
  const [batchIntervalAnchorAt, setBatchIntervalAnchorAt] = useState<string | null>(null);
  const [batchIntervalMinutes, setBatchIntervalMinutes] = useState(300);
  const [batchOwnedByThisInstance, setBatchOwnedByThisInstance] = useState(false);
  const [batchIntervalEditing, setBatchIntervalEditing] = useState(false);
  const [batchIntervalValue, setBatchIntervalValue] = useState("");
  const [batchIntervalInvalid, setBatchIntervalInvalid] = useState(false);
  const batchIntervalInputRef = useRef<HTMLInputElement | null>(null);
  const batchIntervalSkipBlurRef = useRef(false);
  const prevBatchRef = useRef("stopped");
  const [genCli, setGenCli] = useState<"codex" | "claude">(data.plotCli);
  const [translationCli, setTranslationCli] = useState<"codex" | "claude">(data.translationCli);
  const [regenTarget, setRegenTarget] = useState<SeedCandidate | null>(null);
  const [steeringDraft, setSteeringDraft] = useState("");
  const [plotReferExisting, setPlotReferExisting] = useState(true);
  const [generateNewOpen, setGenerateNewOpen] = useState(false);
  const [generateNewPreset, setGenerateNewPreset] = useState(PLOT_PRESETS[0]);
  const [generateNewDirective, setGenerateNewDirective] = useState("");
  const [plotRegenJob, setPlotRegenJob] = useState<{ plotId: number; jobId: number } | null>(null);
  const [plotRegenError, setPlotRegenError] = useState<{ plotId: number; text: string } | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [plotStatusOverrides, setPlotStatusOverrides] = useState<Record<number, { starred?: number }>>({});
  const [sourceDoc, setSourceDoc] = useState<SourceDoc | null>(null);
  const [sourceDocLoading, setSourceDocLoading] = useState(false);
  const [sourceDocError, setSourceDocError] = useState<string | null>(null);
  const [isMobilePlot, setIsMobilePlot] = useState(false);
  const [mobilePlotDetailOpen, setMobilePlotDetailOpen] = useState(false);
  const [mobilePlotLanguage, setMobilePlotLanguage] = useState<PlotLanguageMode>("ko");

  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px), (hover: none) and (pointer: coarse)");
    const update = () => {
      setIsMobilePlot(query.matches || document.documentElement.dataset.mobileRequest === "true");
    };
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isMobilePlot) {
      setMobilePlotDetailOpen(false);
    }
  }, [isMobilePlot]);

  useEffect(() => {
    function handlePopState(event: PopStateEvent) {
      if (isMobilePlot && isPlotMobileDetailHistoryState(event.state)) {
        if (typeof event.state.story37PlotId === "number") {
          setSelectedPlotId(event.state.story37PlotId);
        }
        setMobilePlotLanguage("ko");
        setMobilePlotDetailOpen(true);
        return;
      }
      setMobilePlotDetailOpen(false);
    }
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isMobilePlot]);

  useEffect(() => {
    setMobilePlotDetailOpen(false);
  }, [data.selectedOccupationId]);

  useEffect(() => {
    setPlotStatusOverrides({});
  }, [data.selectedOccupationId]);

  useEffect(() => {
    if (mobilePlotDetailOpen) {
      document.documentElement.dataset.plotMobileDetail = "true";
    } else {
      delete document.documentElement.dataset.plotMobileDetail;
    }
    return () => {
      delete document.documentElement.dataset.plotMobileDetail;
    };
  }, [mobilePlotDetailOpen]);

  useEffect(() => {
    try {
      setGenCli(data.plotCli);
      setTranslationCli(data.translationCli);
    } catch {
      // ignore storage failures
    }
  }, [data.plotCli, data.translationCli]);
  useEffect(() => {
    try {
      localStorage.setItem("story37.plotSeed.genCli", genCli);
    } catch {
      // ignore storage failures
    }
  }, [genCli]);
  useEffect(() => {
    try {
      localStorage.setItem("story37.plotSeed.translationCli", translationCli);
    } catch {
      // ignore storage failures
    }
  }, [translationCli]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(BATCH_INTERVAL_TIME_STORAGE_KEY);
      setBatchIntervalValue(normalizeBatchStartTime(saved || "") || defaultBatchIntervalTime());
    } catch {
      setBatchIntervalValue(defaultBatchIntervalTime());
    }
  }, []);

  useEffect(() => {
    if (batchIntervalEditing) {
      batchIntervalInputRef.current?.focus();
      batchIntervalInputRef.current?.select();
    }
  }, [batchIntervalEditing]);

  const exploreSplit = useSplit(EXPLORE_SPLIT_STORAGE_KEY, 0.4, "x");

  useEffect(() => {
    if (!jobId) {
      return;
    }
    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const response = await fetch(`/api/plot-seed/job-status?jobId=${jobId}`, { cache: "no-store" });
        if (!response.ok || !active) {
          return;
        }
        const status = (await response.json()) as { status?: string; errorText?: string };
        if (active && (status.status === "done" || status.status === "failed" || status.status === "paused")) {
          setGenerating(false);
          setGeneratingAction(null);
          setJobId(null);
          if (status.status === "failed") {
            setGenError(status.errorText || "generation failed (see /var/log/story37.log)");
          } else if (status.status === "paused") {
            setGenError(status.errorText || "generation paused; resume when the CLI is available again");
          }
          router.refresh();
        } else if (active && (status.status === "pending" || status.status === "running")) {
          router.refresh();
        }
      } catch {
        // transient poll failure; keep polling
      }
    }, 2500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [jobId, router]);

  useEffect(() => {
    if (!plotRegenJob) {
      return;
    }
    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const response = await fetch(`/api/plot-seed/job-status?jobId=${plotRegenJob.jobId}`, { cache: "no-store" });
        if (!response.ok || !active) {
          return;
        }
        const status = (await response.json()) as { status?: string; errorText?: string };
        if (active && (status.status === "done" || status.status === "failed" || status.status === "paused")) {
          setPlotRegenJob(null);
          if (status.status === "failed") {
            setPlotRegenError({
              plotId: plotRegenJob.plotId,
              text: status.errorText || "plot regeneration failed (see /var/log/story37.log)"
            });
          } else if (status.status === "paused") {
            setPlotRegenError({
              plotId: plotRegenJob.plotId,
              text: status.errorText || "plot regeneration paused; resume when the CLI is available again"
            });
          }
          router.refresh();
        } else if (active && (status.status === "pending" || status.status === "running")) {
          router.refresh();
        }
      } catch {
        // transient poll failure; keep polling
      }
    }, 2500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [plotRegenJob, router]);

  const visibleProject = useMemo(() => {
    if (!project || Object.keys(plotStatusOverrides).length === 0) {
      return project;
    }
    return {
      ...project,
      plots: project.plots.map((plot) => {
        const override = plotStatusOverrides[plot.id];
        return override ? { ...plot, ...override } : plot;
      })
    };
  }, [project, plotStatusOverrides]);

  const selectedPlot = useMemo(
    () => choosePlotForSelection(visibleProject?.plots ?? [], selectedPlotId, selectedPlotKey),
    [visibleProject, selectedPlotId, selectedPlotKey]
  );

  useEffect(() => {
    if (!selectedPlot) {
      if (selectedPlotId != null) {
        setSelectedPlotId(null);
      }
      return;
    }
    if (selectedPlot.id !== selectedPlotId) {
      setSelectedPlotId(selectedPlot.id);
    }
    const key = plotSelectionKey(selectedPlot);
    if (key && key !== selectedPlotKey) {
      setSelectedPlotKey(key);
    }
  }, [selectedPlot, selectedPlotId, selectedPlotKey]);

  function updateMobilePlotDetailHistory(plotId: number, replace: boolean) {
    if (!isMobilePlot || typeof window === "undefined") {
      return;
    }
    try {
      const currentState = window.history.state;
      if (isPlotMobileDetailHistoryState(currentState) && currentState.story37PlotId === plotId) {
        return;
      }
      const nextState =
        currentState && typeof currentState === "object"
          ? { ...currentState, story37PlotMobileDetail: true, story37PlotId: plotId }
          : { story37PlotMobileDetail: true, story37PlotId: plotId };
      if (replace) {
        window.history.replaceState(nextState, "", window.location.href);
      } else {
        window.history.pushState(nextState, "", window.location.href);
      }
    } catch {
      return;
    }
  }

  function selectPlot(plotId: number, options: SelectPlotOptions = {}) {
    const plot = visibleProject?.plots.find((candidate) => candidate.id === plotId) ?? null;
    const key = plot ? plotSelectionKey(plot) : "";
    if (key) {
      setSelectedPlotKey(key);
    }
    setSelectedPlotId(plotId);
    if (isMobilePlot) {
      if (options.resetMobileLanguage !== false) {
        setMobilePlotLanguage("ko");
      }
      setMobilePlotDetailOpen(true);
      updateMobilePlotDetailHistory(plotId, Boolean(options.replaceMobileHistory));
    }
  }

  function closeMobilePlotDetail() {
    if (typeof window !== "undefined" && isPlotMobileDetailHistoryState(window.history.state)) {
      window.history.back();
      return;
    }
    setMobilePlotDetailOpen(false);
  }

  async function run(action: string, payload: Record<string, unknown>) {
    setBusy(true);
    try {
      await postAction(action, { ...payload, cli: genCli, translationCli });
      router.refresh();
    } catch (error) {
      console.error(error);
    } finally {
      setBusy(false);
    }
  }

  async function ratePlot(plot: SeedCandidate, starred: number) {
    const next = plot.starred === starred ? 0 : starred;
    setPlotStatusOverrides((current) => ({ ...current, [plot.id]: { ...current[plot.id], starred: next } }));
    try {
      await postAction("rate-plot", { plotId: plot.id, starred: next, cli: genCli, translationCli });
    } catch (error) {
      console.error(error);
      setPlotStatusOverrides((current) => ({ ...current, [plot.id]: { ...current[plot.id], starred: plot.starred } }));
    }
  }

  async function openOccupationInfo() {
    if (!data.selectedOccupationId) {
      return;
    }
    setSourceDoc(null);
    setSourceDocError(null);
    setSourceDocLoading(true);
    try {
      setSourceDoc(await fetchSourceDoc(new URLSearchParams({ kind: "occupation", id: String(data.selectedOccupationId) })));
    } catch (error) {
      setSourceDocError(error instanceof Error ? error.message : String(error));
    } finally {
      setSourceDocLoading(false);
    }
  }

  async function openPlotGuideInfo(plot: SeedCandidate) {
    const file = plot.guideFiles[0] || "";
    const params = new URLSearchParams({ kind: "plot-guide", preset: plot.iterationKey || plot.preset });
    if (file) {
      params.set("file", file);
    }
    setSourceDoc(null);
    setSourceDocError(null);
    setSourceDocLoading(true);
    try {
      setSourceDoc(await fetchSourceDoc(params));
    } catch (error) {
      setSourceDocError(error instanceof Error ? error.message : String(error));
    } finally {
      setSourceDocLoading(false);
    }
  }

  useEffect(() => {
    let active = true;
    async function poll() {
      try {
        const response = await fetch("/api/plot-seed/batch-status", { cache: "no-store" });
        if (!response.ok || !active) {
          return;
        }
        const data = (await response.json()) as {
          status?: string;
          scheduledAt?: string | null;
          secondsUntilStart?: number | null;
          cli?: string;
          intervalEnabled?: boolean;
          intervalAnchorAt?: string | null;
          intervalMinutes?: number;
          scheduledOwnerInstance?: string;
          intervalOwnerInstance?: string;
          thisInstanceId?: string;
        };
        if (!active || !data.status) {
          return;
        }
        setBatchStatus(data.status);
        setBatchScheduledAt(data.scheduledAt || null);
        setBatchSeconds(typeof data.secondsUntilStart === "number" ? data.secondsUntilStart : null);
        setBatchCli(data.cli || "");
        setBatchIntervalEnabled(Boolean(data.intervalEnabled));
        setBatchIntervalAnchorAt(data.intervalAnchorAt || null);
        setBatchIntervalMinutes(typeof data.intervalMinutes === "number" ? data.intervalMinutes : 300);
        setBatchOwnedByThisInstance(
          Boolean(data.intervalOwnerInstance || data.scheduledOwnerInstance) &&
            (data.intervalOwnerInstance || data.scheduledOwnerInstance) === data.thisInstanceId
        );
        const scheduledClock =
          formatBatchScheduledClock(data.scheduledAt || null) ||
          formatBatchIntervalAnchorClock(data.intervalAnchorAt || null, typeof data.intervalMinutes === "number" ? data.intervalMinutes : 300);
        if (scheduledClock) {
          setBatchIntervalValue(scheduledClock);
          try {
            localStorage.setItem(BATCH_INTERVAL_TIME_STORAGE_KEY, scheduledClock);
          } catch {
            // ignore storage failures
          }
        }
        const wasActive = prevBatchRef.current === "running" || prevBatchRef.current === "scheduled";
        prevBatchRef.current = data.status;
        // 실행/예약 중에는 진행률을 갱신하고, 멈출 때 한 번 더 새로고침한다.
        if (data.status === "running" || (wasActive && data.status === "stopped")) {
          router.refresh();
        }
      } catch {
        // transient poll failure; keep polling
      }
    }
    poll();
    const timer = window.setInterval(poll, 4000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [router]);

  async function toggleBatch() {
    try {
      const response = await fetch("/api/plot-seed", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "toggle-batch", cli: genCli, translationCli })
      });
      const data = (await response.json()) as {
        status?: string;
        scheduledAt?: string | null;
        secondsUntilStart?: number | null;
        cli?: string;
        intervalEnabled?: boolean;
        intervalAnchorAt?: string | null;
        intervalMinutes?: number;
        scheduledOwnerInstance?: string;
        intervalOwnerInstance?: string;
        thisInstanceId?: string;
      };
      if (data.status) {
        setBatchStatus(data.status);
        setBatchScheduledAt(data.scheduledAt || null);
        setBatchSeconds(typeof data.secondsUntilStart === "number" ? data.secondsUntilStart : null);
        setBatchCli(data.cli || "");
        setBatchIntervalEnabled(Boolean(data.intervalEnabled));
        setBatchIntervalAnchorAt(data.intervalAnchorAt || null);
        setBatchIntervalMinutes(typeof data.intervalMinutes === "number" ? data.intervalMinutes : 300);
        setBatchOwnedByThisInstance(
          Boolean(data.intervalOwnerInstance || data.scheduledOwnerInstance) &&
            (data.intervalOwnerInstance || data.scheduledOwnerInstance) === data.thisInstanceId
        );
        prevBatchRef.current = data.status;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function setBatchIntervalTime(value: string) {
    const normalized = normalizeBatchStartTime(value);
    if (!normalized) {
      setBatchIntervalInvalid(true);
      batchIntervalInputRef.current?.focus();
      return;
    }
    setBatchIntervalInvalid(false);
    try {
      const response = await fetch("/api/plot-seed", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "schedule-batch-interval", cli: genCli, translationCli, startTime: normalized })
      });
      const data = (await response.json()) as {
        status?: string;
        scheduledAt?: string | null;
        secondsUntilStart?: number | null;
        cli?: string;
        intervalEnabled?: boolean;
        intervalAnchorAt?: string | null;
        intervalMinutes?: number;
        scheduledOwnerInstance?: string;
        intervalOwnerInstance?: string;
        thisInstanceId?: string;
      };
      if (data.status) {
        setBatchStatus(data.status);
        setBatchScheduledAt(data.scheduledAt || null);
        setBatchSeconds(typeof data.secondsUntilStart === "number" ? data.secondsUntilStart : null);
        setBatchCli(data.cli || genCli);
        setBatchIntervalEnabled(Boolean(data.intervalEnabled));
        setBatchIntervalAnchorAt(data.intervalAnchorAt || null);
        setBatchIntervalMinutes(typeof data.intervalMinutes === "number" ? data.intervalMinutes : 300);
        setBatchOwnedByThisInstance(
          Boolean(data.intervalOwnerInstance || data.scheduledOwnerInstance) &&
            (data.intervalOwnerInstance || data.scheduledOwnerInstance) === data.thisInstanceId
        );
        prevBatchRef.current = data.status;
        setBatchIntervalEditing(false);
        setBatchIntervalValue(normalized);
        try {
          localStorage.setItem(BATCH_INTERVAL_TIME_STORAGE_KEY, normalized);
        } catch {
          // ignore storage failures
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function toggleBatchInterval() {
    const normalized = normalizeBatchStartTime(batchIntervalValue) || defaultBatchIntervalTime();
    setBatchIntervalInvalid(false);
    try {
      const response = await fetch("/api/plot-seed", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "toggle-batch-interval", cli: genCli, translationCli, startTime: normalized })
      });
      const data = (await response.json()) as {
        status?: string;
        scheduledAt?: string | null;
        secondsUntilStart?: number | null;
        cli?: string;
        intervalEnabled?: boolean;
        intervalAnchorAt?: string | null;
        intervalMinutes?: number;
        scheduledOwnerInstance?: string;
        intervalOwnerInstance?: string;
        thisInstanceId?: string;
      };
      if (data.status) {
        setBatchStatus(data.status);
        setBatchScheduledAt(data.scheduledAt || null);
        setBatchSeconds(typeof data.secondsUntilStart === "number" ? data.secondsUntilStart : null);
        setBatchCli(data.cli || genCli);
        setBatchIntervalEnabled(Boolean(data.intervalEnabled));
        setBatchIntervalAnchorAt(data.intervalAnchorAt || null);
        setBatchIntervalMinutes(typeof data.intervalMinutes === "number" ? data.intervalMinutes : 300);
        setBatchOwnedByThisInstance(
          Boolean(data.intervalOwnerInstance || data.scheduledOwnerInstance) &&
            (data.intervalOwnerInstance || data.scheduledOwnerInstance) === data.thisInstanceId
        );
        setBatchIntervalValue(formatBatchScheduledClock(data.scheduledAt || null) || normalized);
        prevBatchRef.current = data.status;
        try {
          localStorage.setItem(BATCH_INTERVAL_TIME_STORAGE_KEY, normalized);
        } catch {
          // ignore storage failures
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function generate(action: string, extra: Record<string, unknown> = {}) {
    setGenerating(true);
    setGeneratingAction(action);
    setGenError(null);
    try {
      const target = project ? { projectId: project.id } : {};
	      const response = await fetch("/api/plot-seed", {
	        method: "POST",
	        headers: { "content-type": "application/json" },
	        body: JSON.stringify({ action, cli: genCli, translationCli, ...target, ...extra })
	      });
	      const data = (await response.json()) as { jobId?: number; error?: string };
	      if (!response.ok) {
	        throw new Error(data.error || `${action} failed`);
	      }
	      if (data.jobId) {
        setJobId(data.jobId);
      } else {
        setGenerating(false);
        setGeneratingAction(null);
        router.refresh();
      }
	    } catch (error) {
	      console.error(error);
	      setGenError(error instanceof Error ? error.message : String(error));
	      setGenerating(false);
	      setGeneratingAction(null);
	    }
  }

  const batchIntervalActive =
    batchIntervalEnabled && batchOwnedByThisInstance && (batchStatus === "scheduled" || batchStatus === "running");
  const batchIntervalClock =
    formatBatchScheduledClock(batchScheduledAt) ||
    formatBatchIntervalAnchorClock(batchIntervalAnchorAt, batchIntervalMinutes) ||
    normalizeBatchStartTime(batchIntervalValue) ||
    "set time";

  async function regenerateOnePlot(plot: SeedCandidate, steering: string, referExisting: boolean) {
    if (!project || plotRegenJob) {
      return;
    }
    setPlotRegenError(null);
    try {
      const response = await fetch("/api/plot-seed", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "regenerate-one-plot",
          cli: genCli,
          translationCli,
          projectId: project.id,
          plotId: plot.id,
          steering,
          referExisting
        })
      });
      const data = (await response.json()) as { jobId?: number };
      if (data.jobId) {
        setPlotRegenJob({ plotId: plot.id, jobId: data.jobId });
      } else {
        router.refresh();
      }
    } catch (error) {
      console.error(error);
      setPlotRegenError({ plotId: plot.id, text: "plot regeneration request failed" });
    }
  }

  return (
    <main className={`page plotSeedPage ${mobilePlotDetailOpen ? "mobilePlotDetailOpen" : ""}`}>
      <header className="pageHeader plotSeedHeader">
        <div className="plotSeedTitleRow">
          <h1>{project ? project.title : "Plots"}</h1>
          <div className="plotSeedBatchRow">
            <button
              type="button"
              className={`plotSeedBtn ${batchStatus === "running" || batchStatus === "scheduled" ? "running" : ""}`}
              onClick={toggleBatch}
              title="batch generate plots for every occupation; one occupation per task unit"
            >
              {batchStatus === "running" ? (
                <LoaderCircle size={14} className="plotSeedSpin" />
              ) : batchStatus === "scheduled" ? (
                <Clock size={14} />
              ) : (
                <Play size={14} />
              )}
              {batchStatus === "running"
                ? "batch on"
                : batchStatus === "scheduled"
                  ? `batch in ${formatBatchCountdown(batchSeconds) || "soon"}`
                  : "batch now"}
            </button>
            <button
              type="button"
              className={`plotSeedBtn ${batchIntervalActive ? "running" : ""}`}
              onClick={() => void toggleBatchInterval()}
              title="toggle a 5-hour recurring batch interval on this dev-server instance"
            >
              <Clock size={14} />
              batch interval
            </button>
            {batchIntervalEditing ? (
              <input
                ref={batchIntervalInputRef}
                className={`plotSeedBatchLaterInput ${batchIntervalInvalid ? "invalid" : ""}`}
                value={batchIntervalValue}
                onChange={(event) => {
                  setBatchIntervalValue(event.target.value);
                  setBatchIntervalInvalid(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    batchIntervalSkipBlurRef.current = true;
                    void setBatchIntervalTime(batchIntervalValue);
                  }
                  if (event.key === "Escape") {
                    batchIntervalSkipBlurRef.current = true;
                    setBatchIntervalEditing(false);
                    setBatchIntervalInvalid(false);
                  }
                }}
                onBlur={() => {
                  if (batchIntervalSkipBlurRef.current) {
                    batchIntervalSkipBlurRef.current = false;
                    return;
                  }
                  if (batchIntervalInvalid) {
                    return;
                  }
                  void setBatchIntervalTime(batchIntervalValue);
                }}
                placeholder="14:00"
              />
            ) : (
              <button
                type="button"
                className={`plotSeedBatchTimeText ${batchIntervalActive ? "active" : ""}`}
                onClick={() => {
                  setBatchIntervalValue(batchIntervalClock === "set time" ? defaultBatchIntervalTime() : batchIntervalClock);
                  setBatchIntervalEditing(true);
                  setBatchIntervalInvalid(false);
                }}
                title="set the next batch interval start time (hh:mm)"
              >
                {batchIntervalClock}
              </button>
            )}
            {batchCli && batchStatus !== "stopped" ? <span className="plotSeedBatchCli">{batchCli}</span> : null}
            <span className="plotSeedCliRadio">
	              <span className="plotSeedCliLabel">plot:</span>
              <span className="plotSeedCliOption active">{genCli}</span>
              <span className="plotSeedCliLabel">translation:</span>
              <span className="plotSeedCliOption active">{translationCli}</span>
            </span>
          </div>
        </div>
        <Link href="/plot/thesaurus" className="plotSeedBtn plotSeedThesaurusBtn">
          <Network size={14} /> Plot Thesaurus
        </Link>

        {genError ? (
          <div className="plotSeedErrorBar" role="alert">
            <span className="plotSeedErrorText">⚠ {genError}</span>
            <button type="button" className="plotSeedErrorClose" onClick={() => setGenError(null)} aria-label="dismiss">
              <X size={14} />
            </button>
          </div>
        ) : null}
      </header>

      <section className="plotSeedWorkspace">
        <aside className="plotSeedList" aria-label="Occupations">
          <div className="plotSeedListHeader">
            <strong>Occupations</strong>
            <span>{data.occupations.length}</span>
          </div>
          {data.occupations.map((occupation) => (
            <Link
              key={occupation.id}
              href={`/plot/explorer?occ=${occupation.id}`}
              className={`plotSeedListItem ${occupation.id === data.selectedOccupationId ? "active" : ""}`}
            >
              <span className={`plotSeedGender ${occupation.gender}`} aria-hidden>
                {occupation.gender === "male" ? "♂" : "♀"}
              </span>
              <span className="plotSeedListName">{occupation.name}</span>
              {occupation.hasContent ? (
                <span className="plotSeedDot has" title="has generated content" />
              ) : occupation.prefetching ? (
                <span className="plotSeedDot prefetch" title="prefetching" />
              ) : null}
            </Link>
          ))}
        </aside>

        {visibleProject ? (
          <div className="plotSeedDetail">
            <SharedBar
              project={visibleProject}
              occupationName={data.selectedOccupation}
              occupationId={data.selectedOccupationId}
              busy={busy}
              generating={generating}
              generatingAction={generatingAction}
              expectedPlotPresets={data.expectedPlotPresets}
              onGeneratePlots={(action) => generate(action)}
              onGenerateNewPlot={() => {
                setGenerateNewPreset(PLOT_PRESETS[0]);
                setGenerateNewDirective("");
                setGenerateNewOpen(true);
              }}
              onOpenOccupationInfo={() => openOccupationInfo()}
            />

	            <ExploreView
	              plots={visibleProject.plots}
                occupationName={data.selectedOccupation}
	              selectedPlot={selectedPlot}
	              onSelectPlot={selectPlot}
	              busy={busy}
	              onArchive={(plot) => run("archive-plot", { plotId: plot.id, archived: !plot.archived })}
	              onRatePlot={(plot, starred) => ratePlot(plot, starred)}
	              onOpenPlotGuide={(plot) => openPlotGuideInfo(plot)}
                onOpenOccupationInfo={() => openOccupationInfo()}
	              regeneratingPlotId={plotRegenJob?.plotId ?? null}
	              plotRegenError={plotRegenError}
	              onDismissPlotRegenError={() => setPlotRegenError(null)}
	              split={exploreSplit}
                mobileDetailOpen={mobilePlotDetailOpen}
                mobileLanguage={mobilePlotLanguage}
                onCloseMobileDetail={closeMobilePlotDetail}
                onMobileLanguageChange={setMobilePlotLanguage}
	            />
          </div>
        ) : data.selectedOccupationId != null ? (
          <div className="plotSeedDetail">
            <StartPanel
              occupationName={data.selectedOccupation}
              gender={data.occupations.find((occ) => occ.id === data.selectedOccupationId)?.gender ?? "female"}
              generating={generating}
              busy={busy}
              onStartBlank={(direction) => run("ensure-project", { occupationId: data.selectedOccupationId, direction })}
              onGenerate={(direction) => generate("generate-plots", { occupationId: data.selectedOccupationId, direction })}
              onSetGender={(g) => run("set-occupation-gender", { occupationId: data.selectedOccupationId, gender: g })}
            />
          </div>
        ) : (
          <div className="plotSeedDetail">
            <div className="plotSeedEmpty">No occupations found.</div>
          </div>
        )}
      </section>

      {sourceDocLoading || sourceDoc || sourceDocError ? (
        <div
          className="plotSeedModalBackdrop"
          onClick={() => {
            setSourceDoc(null);
            setSourceDocError(null);
            setSourceDocLoading(false);
          }}
        >
          <div className="plotSeedModal plotSeedSourceModal" onClick={(event) => event.stopPropagation()}>
            <div className="plotSeedModalHead">
              <div>
                <div className="plotSeedModalTitle">{sourceDoc?.title || "Source"}</div>
                {sourceDoc?.subtitle ? <div className="plotSeedModalDesc">{sourceDoc.subtitle}</div> : null}
              </div>
              <button
                type="button"
                className="plotSeedIconToggle"
                onClick={() => {
                  setSourceDoc(null);
                  setSourceDocError(null);
                  setSourceDocLoading(false);
                }}
                aria-label="close source"
              >
                <X size={14} />
              </button>
            </div>
            {sourceDocLoading ? <div className="plotSeedMuted">Loading source…</div> : null}
            {sourceDocError ? <div className="plotSeedLocalError">{sourceDocError}</div> : null}
            {sourceDoc ? <pre className="plotSeedSourceMarkdown">{sourceDoc.markdown}</pre> : null}
          </div>
        </div>
      ) : null}

      {regenTarget ? (
        <div className="plotSeedModalBackdrop" onClick={() => setRegenTarget(null)}>
          <div className="plotSeedModal" onClick={(event) => event.stopPropagation()}>
            <div className="plotSeedModalTitle">Regenerate this plot</div>
            <div className="plotSeedModalDesc">
              같은 plot type을 최신 direction·캐릭터 설정으로 다시 생성합니다. 기존 plot은 자동 archive됩니다.
            </div>
            <label className="plotSeedCheckRow">
              <input
                type="checkbox"
                checked={plotReferExisting}
                onChange={(event) => setPlotReferExisting(event.currentTarget.checked)}
              />
              refer existing
            </label>
            <div className={`plotSeedOriginalBox ${plotReferExisting ? "" : "dimmed"}`}>
              <div className="plotSeedColLabel">Original English plot</div>
              <h3>{regenTarget.title || "Untitled"}</h3>
              {regenTarget.logline ? <p className="plotSeedPlotLoglineText">{regenTarget.logline}</p> : null}
              <PlotProse text={regenTarget.plotText} />
            </div>
            <textarea
              className="plotSeedTextarea"
              rows={4}
              autoFocus
              placeholder={
                plotReferExisting
                  ? "steering text (optional) — 원문을 기반으로 어떻게 바꿀지"
                  : "steering text (optional) — 기존 내용과 관계없는 새 아이디어/지시"
              }
              value={steeringDraft}
              onChange={(event) => setSteeringDraft(event.target.value)}
            />
            <div className="plotSeedModalActions">
              <button type="button" className="plotSeedBtn ghost" onClick={() => setRegenTarget(null)}>
                취소
              </button>
              <button
                type="button"
                className="plotSeedBtn confirm"
                disabled={Boolean(plotRegenJob)}
                onClick={() => {
                  regenerateOnePlot(regenTarget, steeringDraft, plotReferExisting);
                  setRegenTarget(null);
                }}
              >
                <RefreshCw size={14} /> 재생성
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {generateNewOpen ? (
        <div className="plotSeedModalBackdrop" onClick={() => setGenerateNewOpen(false)}>
          <div className="plotSeedModal" onClick={(event) => event.stopPropagation()}>
            <div className="plotSeedModalTitle">Generate new</div>
            <select
              className="plotSeedSelect grow"
              value={generateNewPreset}
              autoFocus
              onChange={(event) => setGenerateNewPreset(event.target.value)}
            >
              {PLOT_PRESETS.map((preset) => (
                <option key={preset} value={preset}>
                  {preset}
                </option>
              ))}
            </select>
            <textarea
              className="plotSeedTextarea"
              rows={4}
              placeholder="Creative Directive"
              value={generateNewDirective}
              onChange={(event) => setGenerateNewDirective(event.target.value)}
            />
            <div className="plotSeedModalActions">
              <button type="button" className="plotSeedBtn ghost" onClick={() => setGenerateNewOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="plotSeedBtn confirm"
                disabled={generating || !generateNewPreset}
                onClick={() => {
                  generate("generate-new-plot", { preset: generateNewPreset, steering: generateNewDirective });
                  setGenerateNewOpen(false);
                }}
              >
                <Plus size={14} /> generate
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </main>
  );
}

function SharedBar({
  project,
  occupationName,
  occupationId,
  expectedPlotPresets,
  busy,
  generating,
  generatingAction,
  onGeneratePlots,
  onGenerateNewPlot,
  onOpenOccupationInfo
}: {
  project: SeedDetail;
  occupationName: string;
  occupationId: number | null;
  expectedPlotPresets: string[];
  busy: boolean;
  generating: boolean;
  generatingAction: string | null;
  onGeneratePlots: (action: "generate-plots" | "regenerate-plots") => void;
  onGenerateNewPlot: () => void;
  onOpenOccupationInfo: () => void;
}) {
  const expectedSet = useMemo(() => new Set(expectedPlotPresets.filter(Boolean)), [expectedPlotPresets]);
  const activeExpectedKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const plot of project.plots) {
      if (plot.archived) continue;
      const key = plot.iterationKey || plot.preset;
      if (expectedSet.has(key)) {
        keys.add(key);
      }
    }
    return keys;
  }, [expectedSet, project.plots]);
  const expectedCount = expectedSet.size;
  const activeExpectedCount = activeExpectedKeys.size;
  const activePlotCount = project.plots.filter((plot) => !plot.archived).length;
  const currentIterationComplete = expectedCount > 0 && activeExpectedCount >= expectedCount;
  const generationLabel =
    activePlotCount === 0 ? "generate all" : currentIterationComplete ? "regenerate all" : "resume generation";
  const generatingLabel =
    activePlotCount === 0 ? "generating" : currentIterationComplete ? "regenerating" : "resuming";
  const bulkAction: "generate-plots" | "regenerate-plots" = currentIterationComplete
    ? "regenerate-plots"
    : "generate-plots";
  const bulkGenerating = generating && (generatingAction === "generate-plots" || generatingAction === "regenerate-plots");
  const newGenerating = generating && generatingAction === "generate-new-plot";
  const displayOccupation = occupationName || project.title.replace(/^Project:\s*/, "");

  return (
    <section className="plotSeedShared">
      <div className="plotSeedSharedTop">
        <div className="plotSeedDirection">
          <div className="plotSeedDirectionIntro">
            <h2 className="plotSeedOccupationTitle" title={displayOccupation}>
              {displayOccupation}
            </h2>
            <button
              type="button"
              className="plotSeedIconToggle"
              disabled={!occupationId}
              onClick={onOpenOccupationInfo}
              title="occupation info"
              aria-label="Open occupation source"
            >
              <Info size={15} />
            </button>
          </div>
          <button
            type="button"
            className={`plotSeedMiniBtn ${generating ? "running" : ""}`}
            disabled={generating}
            onClick={() => onGeneratePlots(bulkAction)}
          >
            {bulkGenerating ? <LoaderCircle size={13} className="plotSeedSpin" /> : <RefreshCw size={13} />}
            {bulkGenerating ? generatingLabel : generationLabel}
          </button>
          <button
            type="button"
            className={`plotSeedMiniBtn ${newGenerating ? "running" : ""}`}
            disabled={generating}
            onClick={onGenerateNewPlot}
          >
            {newGenerating ? <LoaderCircle size={13} className="plotSeedSpin" /> : <Plus size={13} />}
            {newGenerating ? "generating" : "generate new"}
          </button>
        </div>
      </div>
    </section>
  );
}

function StartPanel({
  occupationName,
  gender,
  generating,
  busy,
  onStartBlank,
  onGenerate,
  onSetGender
}: {
  occupationName: string;
  gender: "female" | "male";
  generating: boolean;
  busy: boolean;
  onStartBlank: (direction: string) => void;
  onGenerate: (direction: string) => void;
  onSetGender: (gender: "female" | "male") => void;
}) {
  const [direction, setDirection] = useState("");
  return (
    <div className="plotSeedStart">
      <h2 className="plotSeedStartTitle">{occupationName || "Occupation"}</h2>
      <p className="plotSeedMuted">
        아직 이 직업의 작품이 시작되지 않았습니다. direction을 정하고 시작하거나, 바로 플롯을 생성하세요.
      </p>
      <div className="plotSeedStartActions">
        <span className="plotSeedFieldLabel">protagonist</span>
        <button
          type="button"
          className={`plotSeedBtn ${gender === "female" ? "primary" : ""}`}
          disabled={busy || generating}
          onClick={() => onSetGender("female")}
        >
          ♀ female
        </button>
        <button
          type="button"
          className={`plotSeedBtn ${gender === "male" ? "primary" : ""}`}
          disabled={busy || generating}
          onClick={() => onSetGender("male")}
        >
          ♂ male
        </button>
      </div>
      <input
        className="plotSeedInput grow"
        placeholder="direction / theme (optional)"
        value={direction}
        onChange={(event) => setDirection(event.target.value)}
      />
      <div className="plotSeedStartActions">
        <button type="button" className="plotSeedBtn" disabled={busy || generating} onClick={() => onStartBlank(direction)}>
          start blank
        </button>
        <button
          type="button"
          className={`plotSeedBtn primary ${generating ? "running" : ""}`}
          disabled={generating}
          onClick={() => onGenerate(direction)}
        >
          {generating ? <LoaderCircle size={14} className="plotSeedSpin" /> : <Sparkles size={14} />}
          {generating ? "generating" : "Generate"}
        </button>
      </div>
    </div>
  );
}

// Break text after each sentence terminator (. ? ! …, with trailing quotes/
// brackets) followed by whitespace or end, so each sentence renders on its own
// line. A dot with no following space (e.g. "U.S.") is left intact.
function splitSentences(text: string): string[] {
  if (!text) {
    return [];
  }
  return text
    .replace(/([.?!…]+["'”’)\]]*)(\s+|$)/g, "$1\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function PlotProse({ text }: { text: string }) {
  return (
    <>
      {splitSentences(text).map((sentence, index) => (
        <p key={index} className="plotSeedProseLine">
          {sentence}
        </p>
      ))}
    </>
  );
}

function ExploreView({
  plots,
  occupationName,
  selectedPlot,
  onSelectPlot,
  busy,
  onArchive,
  onRatePlot,
  onOpenPlotGuide,
  onOpenOccupationInfo,
  regeneratingPlotId,
  plotRegenError,
  onDismissPlotRegenError,
  split,
  mobileDetailOpen,
  mobileLanguage,
  onCloseMobileDetail,
  onMobileLanguageChange
}: {
  plots: SeedCandidate[];
  occupationName: string;
  selectedPlot: SeedCandidate | null;
  onSelectPlot: (id: number, options?: SelectPlotOptions) => void;
  busy: boolean;
  onArchive: (plot: SeedCandidate) => void;
  onRatePlot: (plot: SeedCandidate, starred: number) => void;
  onOpenPlotGuide: (plot: SeedCandidate) => void;
  onOpenOccupationInfo: () => void;
  regeneratingPlotId: number | null;
  plotRegenError: { plotId: number; text: string } | null;
  onDismissPlotRegenError: () => void;
  split: SplitControl;
  mobileDetailOpen: boolean;
  mobileLanguage: PlotLanguageMode;
  onCloseMobileDetail: () => void;
  onMobileLanguageChange: (language: PlotLanguageMode) => void;
}) {
  const [showArchived, setShowArchived] = useState(false);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SHOW_ARCHIVED_STORAGE_KEY);
      if (saved === "1" || saved === "true") {
        setShowArchived(true);
      } else if (saved === "0" || saved === "false") {
        setShowArchived(false);
      }
    } catch {
      // ignore storage failures
    }
  }, []);
  function updateShowArchived(checked: boolean) {
    setShowArchived(checked);
    try {
      localStorage.setItem(SHOW_ARCHIVED_STORAGE_KEY, checked ? "1" : "0");
    } catch {
      // ignore storage failures
    }
  }
  const activePlots = plots.filter((plot) => !plot.archived);
  const archivedPlots = plots.filter((plot) => plot.archived);
  const visiblePlots = showArchived ? [...activePlots, ...archivedPlots] : activePlots;
  const selectedVisibleIndex = selectedPlot ? visiblePlots.findIndex((plot) => plot.id === selectedPlot.id) : -1;
  const previousPlot = selectedVisibleIndex > 0 ? visiblePlots[selectedVisibleIndex - 1] : null;
  const nextPlot =
    selectedVisibleIndex >= 0 && selectedVisibleIndex < visiblePlots.length - 1
      ? visiblePlots[selectedVisibleIndex + 1]
      : null;
  const mobilePlotBodyRef = useRef<HTMLDivElement | null>(null);
  const selectedGenerationMemo = selectedPlot
    ? [selectedPlot.generationCli, selectedPlot.generationModel, selectedPlot.generationEffort].filter(Boolean).join(" / ")
    : "";
  const selectedCostMemo = selectedPlot ? formatApiCost(selectedPlot.generationUsage) : "";
  const regeneratingSelectedPlot = Boolean(selectedPlot && regeneratingPlotId === selectedPlot.id);
  const selectedPlotRegenError = selectedPlot && plotRegenError?.plotId === selectedPlot.id ? plotRegenError.text : "";

  useEffect(() => {
    if (mobileDetailOpen) {
      mobilePlotBodyRef.current?.scrollTo({ top: 0 });
    }
  }, [mobileDetailOpen, mobileLanguage, selectedPlot?.id]);

  function openAdjacentPlot(plot: SeedCandidate | null) {
    if (!plot) {
      return;
    }
    onSelectPlot(plot.id, { replaceMobileHistory: true, resetMobileLanguage: false });
  }

  function renderDetailTop(plot: SeedCandidate) {
    return (
      <div className="plotSeedPlotDetailTop">
        <div className="plotSeedPlotHeaderRow">
          <div className="plotSeedPlotNameActions">
            <h2 className="plotSeedPlotPatternTitle">{displayPlotPattern(plot)}</h2>
            <button
              type="button"
              className="plotSeedIconToggle"
              onClick={() => onOpenPlotGuide(plot)}
              title="plot pattern source"
              aria-label="Open plot pattern source"
            >
              <Info size={15} />
            </button>
          </div>
          <div className="plotSeedPlotActionRow">
            <button
              type="button"
              className={`plotSeedIconAction star ${plot.starred > 0 ? "active" : ""}`}
              disabled={busy || regeneratingSelectedPlot}
              onClick={() => onRatePlot(plot, 1)}
              title="star this plot"
              aria-label="Star this plot"
            >
              <Star size={15} />
            </button>
            <button
              type="button"
              className={`plotSeedIconAction disliked ${plot.starred < 0 ? "active" : ""}`}
              disabled={busy || regeneratingSelectedPlot}
              onClick={() => onRatePlot(plot, -1)}
              title="dislike this plot"
              aria-label="Dislike this plot"
            >
              <OutlinedDownArrowIcon size={15} />
            </button>
          </div>
        </div>
        <div className="plotSeedPlotDetailMeta">
          <span>llm: {selectedGenerationMemo || "—"}</span>
          {selectedCostMemo ? <span>{selectedCostMemo}</span> : null}
          <button
            type="button"
            className={`plotSeedIconAction trash plotSeedDesktopTrashAction ${plot.archived ? "active" : ""}`}
            disabled={busy || regeneratingSelectedPlot}
            onClick={() => onArchive(plot)}
            title={plot.archived ? "restore from soft deletion" : "soft delete this plot"}
            aria-label={plot.archived ? "Restore plot" : "Soft delete plot"}
          >
            <Trash2 size={12} />
          </button>
        </div>
        {selectedPlotRegenError ? (
          <div className="plotSeedLocalError" role="alert">
            <span>{selectedPlotRegenError}</span>
            <button type="button" onClick={onDismissPlotRegenError} aria-label="dismiss plot regeneration error">
              <X size={13} />
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  function renderMobileDetailTop(plot: SeedCandidate) {
    const mobileMeta = [`llm: ${selectedGenerationMemo || "—"}`, selectedCostMemo].filter(Boolean).join(" · ");
    return (
      <>
        <div className="plotSeedMobileCompactHeader">
          <div className="plotSeedMobileCompactRow plotSeedMobileCompactRowTitle">
            <h2 className="plotSeedMobileCompactTitle">{displayPlotPattern(plot)}</h2>
            <button
              type="button"
              className="plotSeedIconToggle plotSeedMobileSourceBtn"
              onClick={() => onOpenPlotGuide(plot)}
              title="plot pattern source"
              aria-label="Open plot pattern source"
            >
              <Info size={13} />
            </button>
          </div>
          <div className="plotSeedMobileCompactRow plotSeedMobileCompactMeta">{mobileMeta}</div>
          <div className="plotSeedMobileCompactRow plotSeedMobileCompactRowControls">
            <div className="plotSeedMobileLanguageToggle" aria-label="Plot language">
              <button
                type="button"
                className={mobileLanguage === "ko" ? "active" : ""}
                onClick={() => onMobileLanguageChange("ko")}
              >
                KO
              </button>
              <button
                type="button"
                className={mobileLanguage === "en" ? "active" : ""}
                onClick={() => onMobileLanguageChange("en")}
              >
                EN
              </button>
            </div>
            <div className="plotSeedMobileCompactActions">
              <button
                type="button"
                className={`plotSeedIconAction star ${plot.starred > 0 ? "active" : ""}`}
                disabled={busy || regeneratingSelectedPlot}
                onClick={() => onRatePlot(plot, 1)}
                title="star this plot"
                aria-label="Star this plot"
              >
                <Star size={18} />
              </button>
              <button
                type="button"
                className={`plotSeedIconAction disliked ${plot.starred < 0 ? "active" : ""}`}
                disabled={busy || regeneratingSelectedPlot}
                onClick={() => onRatePlot(plot, -1)}
                title="dislike this plot"
                aria-label="Dislike this plot"
              >
                <OutlinedDownArrowIcon size={18} />
              </button>
            </div>
          </div>
          <button
            type="button"
            className={`plotSeedIconAction trash plotSeedMobileTrashAction ${plot.archived ? "active" : ""}`}
            disabled={busy || regeneratingSelectedPlot}
            onClick={() => onArchive(plot)}
            title={plot.archived ? "restore from soft deletion" : "soft delete this plot"}
            aria-label={plot.archived ? "Restore plot" : "Soft delete plot"}
          >
            <Trash2 size={14} />
          </button>
        </div>
        {selectedPlotRegenError ? (
          <div className="plotSeedLocalError plotSeedMobileLocalError" role="alert">
            <span>{selectedPlotRegenError}</span>
            <button type="button" onClick={onDismissPlotRegenError} aria-label="dismiss plot regeneration error">
              <X size={13} />
            </button>
          </div>
        ) : null}
      </>
    );
  }

  function renderPlotColumn(plot: SeedCandidate, language: PlotLanguageMode) {
    if (language === "ko") {
      return (
        <div className="plotSeedPlotCol">
          {plot.ko ? (
            <>
              <h3 className="plotSeedPlotTitle">{plot.ko.title || "—"}</h3>
              <p className="plotSeedPlotLoglineText">{plot.ko.logline}</p>
              <PlotProse text={plot.ko.plot} />
            </>
          ) : (
            <p className="plotSeedMuted">· 한국어 번역 대기 중</p>
          )}
        </div>
      );
    }
    return (
      <div className="plotSeedPlotCol">
        <h3 className="plotSeedPlotTitle">{plot.title || "Untitled"}</h3>
        <p className="plotSeedPlotLoglineText">{plot.logline}</p>
        <PlotProse text={plot.plotText} />
      </div>
    );
  }

  function renderItem(plot: SeedCandidate) {
    return (
      <button
        key={plot.id}
        type="button"
        className={`plotSeedExploreItem ${selectedPlot?.id === plot.id ? "active" : ""} ${plot.archived ? "archived" : ""}`}
        onClick={() => onSelectPlot(plot.id)}
      >
        <span className="plotSeedExploreItemRow1">
          <span className="plotSeedExploreItemTitle">
            {plot.starred > 0 ? <span className="plotSeedRatingMark starred">★</span> : null}
            {plot.starred < 0 ? <span className="plotSeedRatingMark disliked" aria-hidden /> : null}
            {plot.ko?.title || plot.title || "(untitled)"}
            {plot.used ? <span className="plotSeedUsedMark" title="sent to project">used</span> : null}
          </span>
          <span className="plotSeedExploreItemPlotName">{displayPlotPattern(plot)}</span>
        </span>
        <span className="plotSeedExploreItemLog">{plot.ko?.logline || plot.logline}</span>
      </button>
    );
  }

  return (
    <div
      className="plotSeedExploreSplit"
      ref={split.containerRef}
      style={{ gridTemplateColumns: `minmax(0, ${split.ratio}fr) 10px minmax(0, ${1 - split.ratio}fr)` }}
    >
      <div className="plotSeedExplorePane">
        <div className="plotSeedSectionHead sticky">
          <strong className="plotSeedCandidateHeaderTitle">
            <span className="plotSeedDesktopCandidateLabel">Plot candidates</span>
            <span className="plotSeedMobileCandidateTitleWrap">
              <span className="plotSeedMobileCandidateLabel">{occupationName || "Plot candidates"}</span>
              <button
                type="button"
                className="plotSeedIconToggle plotSeedMobileOccupationInfoBtn"
                onClick={onOpenOccupationInfo}
                title="occupation info"
                aria-label="Open occupation source"
              >
                <Info size={12} />
              </button>
            </span>
            <span className="plotSeedCandidateCount"> ({activePlots.length})</span>
          </strong>
          <label className="plotSeedSwitch" title="역대(아카이브) 플롯을 리스트 맨 아래에 표시">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => updateShowArchived(event.currentTarget.checked)}
            />
            <span className="plotSeedSwitchTrack">
              <span className="plotSeedSwitchThumb" />
            </span>
            <span className="plotSeedSwitchText">archived{archivedPlots.length ? ` (${archivedPlots.length})` : ""}</span>
          </label>
        </div>
        <div className="plotSeedExploreList">
          {activePlots.length === 0 && !(showArchived && archivedPlots.length > 0) ? (
            <div className="plotSeedMuted plotSeedListEmpty">No candidates yet.</div>
          ) : (
            <>
              {activePlots.map(renderItem)}
              {showArchived && archivedPlots.length > 0 ? (
                <>
                  <div className="plotSeedArchivedDivider">archived ({archivedPlots.length})</div>
                  {archivedPlots.map(renderItem)}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div
        className="plotSeedSplitHandle vertical"
        onPointerDown={split.handle.onPointerDown}
        onPointerMove={split.handle.onPointerMove}
        onPointerUp={split.handle.onPointerUp}
        role="separator"
        aria-orientation="vertical"
      >
        <GripVertical size={14} />
      </div>

      {selectedPlot ? (
        <div className="plotSeedPlotDetailSplit">
          {renderDetailTop(selectedPlot)}
          <div className="plotSeedPlotBilingual">
            {renderPlotColumn(selectedPlot, "ko")}
            {renderPlotColumn(selectedPlot, "en")}
          </div>
        </div>
      ) : (
        <div className="plotSeedPlotDetailSplit empty">No plot selected.</div>
      )}
      {selectedPlot ? (
        <div className={`plotSeedMobilePlotDetail ${mobileDetailOpen ? "open" : ""}`} aria-hidden={!mobileDetailOpen}>
          {renderMobileDetailTop(selectedPlot)}
          <div className="plotSeedMobilePlotBody" ref={mobilePlotBodyRef}>
            {renderPlotColumn(selectedPlot, mobileLanguage)}
          </div>
          <button
            type="button"
            className="plotSeedMobileFloatingBack"
            onClick={onCloseMobileDetail}
            aria-label="Back to plot list"
            title="Back"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="plotSeedMobileCandidateNav" aria-label="Plot candidate navigation">
            <button
              type="button"
              aria-label="Open previous plot candidate"
              disabled={!previousPlot}
              onClick={() => openAdjacentPlot(previousPlot)}
              title="Previous candidate"
            >
              <ChevronUp size={18} />
            </button>
            <button
              type="button"
              aria-label="Open next plot candidate"
              disabled={!nextPlot}
              onClick={() => openAdjacentPlot(nextPlot)}
              title="Next candidate"
            >
              <ChevronDown size={18} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
